import { RichTextEditor, Link, useRichTextEditorContext } from "@mantine/tiptap";
import { EditorEvents, Extensions, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { observer } from "mobx-react";
import { useCallback, useContext, useMemo } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./RichTextEditor.module.css";
import { useTranslation } from "react-i18next";
import { IconCornerDownRight } from "@tabler/icons-react";
import { UI } from "@wwimmo/ui";

function LineBreakControl() {
    const { editor } = useRichTextEditorContext();

    const onClickInsertLineBreak = useCallback(() => {
        editor?.chain().focus().setHardBreak().run();
    }, [editor]);

    const renderTooltip = useMemo(() => {
        return (
            <UI.Tooltip>
                <div style={{ margin: "0.25rem", marginTop: "0rem" }}>
                    Zeilenumbruch <br />
                    <kbd>&#8679; Shift</kbd> + <kbd>&#8629; Enter</kbd>
                </div>
            </UI.Tooltip>
        );
    }, []);

    return (
        <UI.OverlayTrigger placement="top" overlay={renderTooltip}>
            <RichTextEditor.Control onClick={onClickInsertLineBreak}>
                <IconCornerDownRight stroke={1.5} size="1rem" />
            </RichTextEditor.Control>
        </UI.OverlayTrigger>
    );
}

type RichTextEditorProps = {
    /**
     * Content to show in the WYSIWYG editor
     */
    content?: string;

    /**
     * Placeholder text to show when the editor is empty
     */
    placeholder?: string;

    /**
     * If `true`, the editor will check the content for errors on initialization. Emitting the `contentError` event if the content is invalid. Which can be used to show a warning or error message to the user.
     */
    enableContentCheck?: boolean;

    /**
     * Called when the editor encounters an error while parsing the content. Only enabled if `enableContentCheck` is `true`. If not provided uses `console.log`
     */
    onContentError?: (error: EditorEvents["contentError"]) => void;

    /**
     * Called when the editors content is update. If not provided uses `console.log`
     */
    onContentUpdate?: (update: EditorEvents["update"]) => void;

    /**
     * Called when the editor is ready.
     */
    onEditorCreate?: (editor: EditorEvents["create"]) => void;

    /**
     * Called when the editor loses focus.
     */
    onBlur?: (editor: EditorEvents["blur"]) => void;

    /**
     * Determines whether position: sticky styles should be added to the toolbar, `false` by default
     */
    stickyToolbar?: boolean;

    /**
     * When stickyToolbar is enabled, the default offset is either `uiStore.headerHeight + uiStore.navigationBarHeight - 15` or `uiStore.headerHeight - 9` when navbar is hidden
     */
    stickyToolbarOffsetOverride?: number;

    /**
     * When a characterLimit is set the editor will show a character counter. Counts the characters including html tags.
     */
    characterLimit?: number;

    /**
     * If `true`, the editor will be disabled and the user won't be able to edit the content. This is useful for displaying read-only content.
     */
    disabled?: boolean;

    className?: string;
};

function RichTextEditorBase(props: RichTextEditorProps) {
    const { uiStore } = useContext(RootStoreContext);
    const { t } = useTranslation();

    const extensions: Extensions = [StarterKit, Underline, Link.configure({ openOnClick: true })];
    if (props.placeholder) {
        extensions.push(
            Placeholder.configure({
                placeholder: props.placeholder
            })
        );
    }

    const editor = useEditor({
        extensions: extensions,
        content: props.content ?? "",
        enableContentCheck: props.enableContentCheck ?? true,
        onContentError: props.onContentError
            ? props.onContentError
            : (error: EditorEvents["contentError"]) => {
                  console.log("Content error:", error.error);
              },
        onUpdate: props.onContentUpdate
            ? props.onContentUpdate
            : (update: EditorEvents["update"]) => {
                  console.log("Editor content updated:", update.editor.getHTML());
              },
        onCreate: props.onEditorCreate ? props.onEditorCreate : undefined,
        onBlur: props.onBlur ? props.onBlur : undefined,
        editable: props.disabled !== undefined ? !props.disabled : true
    });

    if (!editor) {
        return null;
    }

    const htmlLength = editor.getHTML().length;
    const htmlLengthLimitPercentage = props.characterLimit ? Math.round((100 / props.characterLimit) * htmlLength) : 0;
    const defaultStickyToolbarOffset = uiStore.showNavigationBar
        ? uiStore.headerHeight + uiStore.navigationBarHeight - 15
        : uiStore.headerHeight - 9;
    const stickyToolbarOffset = props.stickyToolbarOffsetOverride
        ? props.stickyToolbarOffsetOverride
        : defaultStickyToolbarOffset;

    return (
        <RichTextEditor
            editor={editor}
            withTypographyStyles={false}
            classNames={{
                toolbar: styles.richTextEditorToolbar,
                content: props.disabled ? styles.richTextEditorReadonly : undefined
            }}
            className={props.className ? props.className : undefined}
        >
            <RichTextEditor.Toolbar
                sticky={props.stickyToolbar}
                stickyOffset={props.stickyToolbar ? stickyToolbarOffset : undefined}
            >
                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                    <RichTextEditor.Strikethrough />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.H1 />
                    <RichTextEditor.H2 />
                    <RichTextEditor.H3 />
                    <RichTextEditor.H4 />
                    <RichTextEditor.H5 />
                    <RichTextEditor.H6 />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <LineBreakControl />
                    <RichTextEditor.ClearFormatting />
                </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content />
            {props.characterLimit ? (
                <div
                    className={
                        htmlLength > props.characterLimit
                            ? `${styles.characterCount} ${styles.characterCountWarning}`
                            : `${styles.characterCount}`
                    }
                >
                    <svg height="20" width="20" viewBox="0 0 20 20">
                        <circle r="10" cx="10" cy="10" fill="#e9ecef" />
                        <circle
                            r="5"
                            cx="10"
                            cy="10"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="10"
                            strokeDasharray={`calc(${htmlLengthLimitPercentage} * 31.4 / 100) 31.4`}
                            transform="rotate(-90) translate(-20)"
                        />
                        <circle r="6" cx="10" cy="10" fill="white" />
                    </svg>
                    {htmlLength} / {props.characterLimit} HTML-{t("labels.characters")}
                </div>
            ) : null}
        </RichTextEditor>
    );
}

export default observer(RichTextEditorBase);
